import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { ArrowLeft, Clock } from "@phosphor-icons/react";

// Very small markdown → HTML for headings, paragraphs, bullets, bold.
function renderBody(md = "") {
  const escape = (s) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushList(); out.push(""); continue; }
    if (line.startsWith("### ")) { flushList(); out.push(`<h3>${escape(line.slice(4))}</h3>`); continue; }
    if (line.startsWith("## "))  { flushList(); out.push(`<h2>${escape(line.slice(3))}</h2>`); continue; }
    if (line.startsWith("# "))   { flushList(); out.push(`<h1>${escape(line.slice(2))}</h1>`); continue; }
    if (/^[-*]\s/.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${escape(line.replace(/^[-*]\s/, ""))}</li>`);
      continue;
    }
    flushList();
    let html = escape(line)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, "<code>$1</code>");
    out.push(`<p>${html}</p>`);
  }
  flushList();
  return out.join("\n");
}

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/articles/${slug}`).then(({ data }) => setArticle(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="px-6 md:px-14 py-24 min-h-[60vh]">
        <div className="text-overline mb-4">◢ 404</div>
        <h1 className="font-display text-4xl text-white">Article not found.</h1>
        <Link to="/knowledge" className="btn-secondary mt-8 inline-flex"><ArrowLeft size={14} /> Back to Knowledge Centre</Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="px-6 md:px-14 py-24 min-h-[60vh]">
        <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#8E8E93]">loading…</div>
      </div>
    );
  }

  return (
    <div data-testid="article-page" className="px-6 md:px-14 py-16 md:py-24">
      <Link to="/knowledge" className="btn-ghost"><ArrowLeft size={14} /> Knowledge Centre</Link>

      <article className="mt-8 max-w-3xl">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">{article.category}</span>
          <span className="font-mono text-[10px] text-[#8E8E93] flex items-center gap-1"><Clock size={11} /> {article.read_minutes} min</span>
        </div>
        <h1 className="mt-4 font-display font-bold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.05]">
          {article.title}
        </h1>
        <p className="mt-5 text-[#D7D7D9] text-lg leading-relaxed">{article.summary}</p>

        {article.cover_image && (
          <div className="mt-10 border border-[#2A2A2A] overflow-hidden">
            <img src={mediaUrl(article.cover_image)} alt={article.title} className="w-full h-auto object-cover" />
          </div>
        )}

        <div
          className="mt-10 ww-article-body"
          dangerouslySetInnerHTML={{ __html: renderBody(article.body) }}
        />

        <div className="mt-16 ww-card p-6">
          <div className="text-overline mb-2">◢ Working on this?</div>
          <div className="font-display text-2xl text-white">Talk to a senior engineer.</div>
          <p className="mt-2 text-[#8E8E93] text-sm">Open the Project Router or try the AI Scoper.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary">Project Router →</Link>
            <Link to="/scoper"  className="btn-secondary">AI Scoper</Link>
          </div>
        </div>
      </article>
    </div>
  );
}
